import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Mail } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Logo } from '@/components/brand/Logo';
import { CtaButton } from '@/components/site/CtaButton';
import { WhatsAppButton } from '@/components/site/WhatsAppButton';
import { LanguageSwitcher } from '@/components/site/LanguageSwitcher';
import { Container } from '@/components/layout/Container';
import { AGENCY, CONSULTATION_PATH, NAV_LINKS } from '@/lib/site';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300',
        scrolled
          ? 'border-[color:var(--rmh-border)] shadow-[var(--shadow-hairline)]'
          : 'border-transparent',
      )}
      style={{
        backgroundColor: scrolled ? 'rgba(250,247,242,0.94)' : 'var(--rmh-canvas)',
        backdropFilter: scrolled ? 'saturate(140%) blur(10px)' : 'none',
      }}
    >
      <Container wide className="flex h-[72px] items-center justify-between gap-4 sm:h-[84px]">
        <Logo variant="lockup" testId="site-header-logo-link" />

        <nav
          data-testid="site-header-nav"
          className="hidden items-center gap-8 lg:flex"
          aria-label={t.header.primaryNavLabel}
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              data-testid={`site-header-nav-${link.key}-link`}
              className={({ isActive }) =>
                cn(
                  'rmh-link-underline text-[0.78rem] font-medium uppercase tracking-[0.16em] transition-colors duration-300',
                  isActive
                    ? 'text-[color:var(--rmh-ink)]'
                    : 'text-[color:var(--rmh-muted)] hover:text-[color:var(--rmh-ink)]',
                )
              }
            >
              {t.nav[link.key]}
            </NavLink>
          ))}
        </nav>

        {/* One switcher only — always visible, so its testid stays unique
            and it works at every breakpoint. */}
        <div className="flex items-center gap-2 lg:gap-2.5">
          <LanguageSwitcher />

          <div className="hidden items-center gap-2.5 lg:flex">
            <WhatsAppButton
              variant="outline"
              size="sm"
              testId="site-header-whatsapp-link"
            />
            <CtaButton
              to={CONSULTATION_PATH}
              size="sm"
              variant="primary"
              arrow
              testId="site-header-book-consultation-button"
            >
              {t.common.bookConsultation}
            </CtaButton>
          </div>

          <button
            type="button"
            aria-label={open ? t.header.closeMenu : t.header.openMenu}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="site-header-mobile-menu-button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--rmh-border-strong)] bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)] transition-colors duration-300 hover:border-[color:var(--rmh-ink)] lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </Container>

      <AnimatePresence>
        {open ? (
          <motion.div
            key="mobile-drawer"
            data-testid="site-header-mobile-drawer"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden border-t border-[color:var(--rmh-border)]"
            style={{ backgroundColor: 'var(--rmh-canvas)' }}
          >
            <Container className="py-7">
              <nav className="flex flex-col" aria-label={t.header.mobileNavLabel}>
                {NAV_LINKS.map((link, i) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`site-header-mobile-nav-${link.key}-link`}
                    className="flex items-baseline justify-between border-b border-[color:var(--rmh-border)] py-4 font-serif text-2xl text-[color:var(--rmh-ink)]"
                  >
                    {t.nav[link.key]}
                    <span className="rmh-num text-[0.65rem] text-[color:var(--rmh-muted-2)]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </Link>
                ))}
              </nav>

              <div className="mt-7 flex flex-col gap-3">
                <CtaButton
                  to={CONSULTATION_PATH}
                  size="lg"
                  variant="primary"
                  arrow
                  testId="site-header-mobile-book-consultation-button"
                >
                  {t.common.bookConsultation}
                </CtaButton>
                <WhatsAppButton
                  variant="outline"
                  size="lg"
                  long
                  testId="site-header-mobile-whatsapp-link"
                />
                <a
                  href={`mailto:${AGENCY.email}`}
                  data-testid="site-header-mobile-email-link"
                  className="mt-1 inline-flex items-center gap-2 text-[0.82rem] text-[color:var(--rmh-muted)]"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {AGENCY.email}
                </a>

                <div className="rmh-rule mt-3" />
                <LanguageSwitcher variant="block" className="mt-1" />
              </div>
            </Container>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};

export default Header;
